(function (root, factory) {
  'use strict';

  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else {
    root.GiftChoiceCore = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const DECISION_STORAGE_KEY = 'gift_system_level_decisions_v2';

  function normalizeGiftOptions(resolvedProducts) {
    if (!Array.isArray(resolvedProducts)) {
      return [];
    }

    const seenHandles = new Set();
    const seenVariantIds = new Set();
    const options = [];

    resolvedProducts.forEach((product) => {
      if (!product || typeof product.handle !== 'string' || !Array.isArray(product.variants)) {
        return;
      }

      const handle = product.handle.trim().toLowerCase();
      const variant = product.variants[0];

      if (
        !handle ||
        !variant ||
        variant.id == null ||
        variant.available !== true
      ) {
        return;
      }

      const variantKey = String(variant.id);

      if (seenHandles.has(handle) || seenVariantIds.has(variantKey)) {
        return;
      }

      seenHandles.add(handle);
      seenVariantIds.add(variantKey);
      options.push({
        handle,
        productId: product.id ?? null,
        variantId: variant.id,
        title: product.title || '',
        image: product.featured_image || product.image?.src || null,
        price: variant.price ?? 0,
        available: true,
      });
    });

    return options;
  }

  function buildOptionSignature(options) {
    if (!Array.isArray(options)) {
      return '';
    }

    const pairs = new Set();

    options.forEach((option) => {
      if (
        !option ||
        typeof option.handle !== 'string' ||
        option.variantId == null
      ) {
        return;
      }

      const handle = option.handle.trim().toLowerCase();
      const variantId = String(option.variantId);

      if (!handle || !variantId) {
        return;
      }

      pairs.add(`${handle}:${variantId}`);
    });

    return Array.from(pairs).sort().join('|');
  }

  function resolveOptionCardinality(options) {
    if (!Array.isArray(options) || options.length === 0) {
      return { mode: 'unavailable', option: null };
    }

    if (options.length === 1) {
      return { mode: 'auto', option: options[0] };
    }

    return { mode: 'choose', option: null };
  }

  function isRecord(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  function getScopeKeys(scope, needsSignature) {
    if (
      !isRecord(scope) ||
      typeof scope.cartToken !== 'string' ||
      scope.cartToken.length === 0 ||
      scope.levelNumber == null
    ) {
      return null;
    }

    const levelKey = String(scope.levelNumber);

    if (!levelKey || (needsSignature && typeof scope.signature !== 'string')) {
      return null;
    }

    return {
      cartToken: scope.cartToken,
      levelKey,
    };
  }

  function getNoneDecision(store, scope) {
    const keys = getScopeKeys(scope, true);

    if (!isRecord(store) || !keys) {
      return null;
    }

    const tokenDecisions = store[keys.cartToken];
    const storedDecision = isRecord(tokenDecisions)
      ? tokenDecisions[keys.levelKey]
      : null;

    if (
      !isRecord(storedDecision) ||
      storedDecision.decision !== 'none' ||
      storedDecision.signature !== scope.signature
    ) {
      return null;
    }

    return {
      decision: 'none',
      signature: storedDecision.signature,
    };
  }

  function recordNoneDecision(store, scope) {
    const root = isRecord(store) ? { ...store } : {};
    const keys = getScopeKeys(scope, true);

    if (!keys) {
      return root;
    }

    const currentTokenDecisions = isRecord(root[keys.cartToken])
      ? root[keys.cartToken]
      : {};

    root[keys.cartToken] = {
      ...currentTokenDecisions,
      [keys.levelKey]: {
        decision: 'none',
        signature: scope.signature,
      },
    };

    return root;
  }

  function clearNoneDecision(store, scope) {
    const root = isRecord(store) ? { ...store } : {};
    const keys = getScopeKeys(scope, false);

    if (!keys || !isRecord(root[keys.cartToken])) {
      return root;
    }

    const tokenDecisions = { ...root[keys.cartToken] };

    delete tokenDecisions[keys.levelKey];

    if (Object.keys(tokenDecisions).length === 0) {
      delete root[keys.cartToken];
    } else {
      root[keys.cartToken] = tokenDecisions;
    }

    return root;
  }

  function resolveLevelState(input) {
    const source = isRecord(input) ? input : {};
    const levelNumber = source.levelNumber;
    const options = Array.isArray(source.options) ? source.options : [];
    const cartItems = Array.isArray(source.cartItems) ? source.cartItems : [];
    const decision = source.decision;
    const signature = buildOptionSignature(options);
    const giftLine = levelNumber == null
      ? null
      : cartItems.find((item) => (
        isRecord(item) &&
        item.quantity > 0 &&
        isRecord(item.properties) &&
        item.properties._is_gift === 'true' &&
        String(item.properties._gift_level) === String(levelNumber)
      )) || null;
    const baseResolution = {
      levelNumber,
      status: null,
      action: null,
      signature,
      giftLineKey: giftLine ? giftLine.key || null : null,
      option: null,
      clearDecision: false,
    };

    if (source.eligible !== true) {
      return {
        ...baseResolution,
        action: 'cleanup',
        clearDecision: true,
      };
    }

    if (giftLine) {
      return {
        ...baseResolution,
        status: 'gift',
        clearDecision: decision != null,
      };
    }

    if (
      isRecord(decision) &&
      decision.decision === 'none' &&
      decision.signature === signature
    ) {
      return {
        ...baseResolution,
        status: 'none',
      };
    }

    const cardinality = resolveOptionCardinality(options);
    const clearDecision = decision != null;

    if (cardinality.mode === 'unavailable') {
      return {
        ...baseResolution,
        status: 'unavailable',
        clearDecision,
      };
    }

    if (cardinality.mode === 'auto') {
      return {
        ...baseResolution,
        action: 'auto-add',
        option: cardinality.option,
        clearDecision,
      };
    }

    return {
      ...baseResolution,
      status: 'pending',
      action: 'choose',
      clearDecision,
    };
  }

  function sortPendingLevels(levelResolutions) {
    if (!Array.isArray(levelResolutions)) {
      return [];
    }

    return levelResolutions
      .filter((resolution) => (
        isRecord(resolution) && resolution.status === 'pending'
      ))
      .sort((first, second) => (
        Number(first.levelNumber) - Number(second.levelNumber)
      ));
  }

  function shouldBlockCheckout(levelResolutions) {
    return Array.isArray(levelResolutions) && levelResolutions.some(
      (resolution) => (
        isRecord(resolution) && resolution.status === 'pending'
      ),
    );
  }

  function isGiftCartLine(item) {
    return (
      isRecord(item) &&
      isRecord(item.properties) &&
      item.properties._is_gift === 'true'
    );
  }

  function matchesTriggerProduct(item, triggerProduct) {
    if (!isRecord(item)) {
      return false;
    }

    // triggerProduct pode ser string (handle/id) ou objeto { handle: "..." },
    // exatamente como as três superfícies aceitam hoje.
    const productIdentifier = isRecord(triggerProduct)
      ? triggerProduct.handle
      : triggerProduct;

    // Normalizar: remover GID se presente (gid://shopify/Product/123 -> "123").
    const cleanIdentifier = productIdentifier && productIdentifier.toString().includes('gid://shopify/Product/')
      ? productIdentifier.toString().split('/').pop()
      : productIdentifier;

    const matchById = item.product_id == cleanIdentifier ||
      item.product_id === parseInt(cleanIdentifier, 10);
    const matchByHandle = item.handle &&
      (item.handle === cleanIdentifier || item.handle === productIdentifier);

    return matchById || matchByHandle;
  }

  function resolveTriggerProgress(input) {
    const source = isRecord(input) ? input : {};
    const triggerProducts = Array.isArray(source.triggerProducts) ? source.triggerProducts : [];

    // Nível sem lista de gatilhos depende só do valor mínimo (TRIG-04).
    if (triggerProducts.length === 0) {
      return { required: 0, matched: 0, missing: 0, satisfied: true };
    }

    const cartItems = Array.isArray(source.cartItems) ? source.cartItems : [];
    const eligibleItems = cartItems.filter((item) => isRecord(item) && !isGiftCartLine(item));
    const countMode = source.countMode === 'units' ? 'units' : 'distinct';
    const requestedRaw = Number(source.requiredCount);
    const requestedCount = Number.isFinite(requestedRaw) && requestedRaw > 0 ? requestedRaw : 1;

    let matched;
    let required;

    if (countMode === 'units') {
      // Soma as quantidades das linhas que batem, sem teto: repetir o mesmo
      // item é legítimo nesse modo.
      matched = eligibleItems.reduce((total, item) => {
        const isTrigger = triggerProducts.some((triggerProduct) => matchesTriggerProduct(item, triggerProduct));

        if (!isTrigger) {
          return total;
        }

        const quantity = Number(item.quantity);
        return total + (Number.isFinite(quantity) && quantity > 0 ? quantity : 0);
      }, 0);
      required = requestedCount;
    } else {
      // Conta quantos gatilhos distintos da lista estão presentes, sem
      // considerar quantidade, e a exigência nunca passa do tamanho da lista
      // (clamp, decisão travada 6).
      matched = triggerProducts.filter((triggerProduct) => (
        eligibleItems.some((item) => matchesTriggerProduct(item, triggerProduct))
      )).length;
      required = Math.min(requestedCount, triggerProducts.length);
    }

    return {
      required,
      matched,
      missing: Math.max(required - matched, 0),
      satisfied: matched >= required,
    };
  }

  /**
   * Escolhe qual nível a interface está tratando: o atual (último já
   * atingido) e o próximo (primeiro ainda não atingido), a partir dos
   * níveis configurados e do total elegível do carrinho. Único lugar que
   * decide isso — barra do quick cart, barra da página /cart, card de
   * campanha e botão de produtos usam esta função em vez de cada um
   * ordenar e filtrar os níveis por conta própria.
   *
   * Nível ativo = enabled true e com ao menos um brinde disponível; nível
   * sem brinde disponível ou desativado não entra na escolha. Ordenação
   * por valor mínimo crescente; próximo é o primeiro cujo mínimo ainda não
   * foi atingido, atual é o último já atingido; alvo é o próximo quando
   * existe e o atual quando não existe.
   */
  function resolveTargetLevel(input) {
    const source = isRecord(input) ? input : {};
    const rawLevels = Array.isArray(source.levels) ? source.levels : [];
    const cartTotalRaw = Number(source.cartTotal);
    const cartTotal = Number.isFinite(cartTotalRaw) ? cartTotalRaw : 0;

    const activeLevels = rawLevels.filter((level) => (
      isRecord(level) &&
      Boolean(level.enabled) &&
      Array.isArray(level.availableGifts) &&
      level.availableGifts.length > 0
    ));

    const sortedLevels = activeLevels.slice().sort((a, b) => a.minValue - b.minValue);

    const nextLevel = sortedLevels.find((level) => cartTotal < level.minValue) || null;
    const currentLevel = sortedLevels.filter((level) => cartTotal >= level.minValue).pop() || null;
    const targetLevel = nextLevel || currentLevel || null;

    return { currentLevel, nextLevel, targetLevel };
  }

  function resolveCampaignSchedule(input) {
    const source = isRecord(input) ? input : {};

    // Agendamento desligado: o mestre (gift_system_enabled) manda, sempre ativo.
    if (source.enabled !== true) {
      return { active: true, status: 'disabled' };
    }

    const now = Number(source.nowEpoch);

    // Sem relógio confiável: fail-open, não bloqueia a campanha.
    if (!Number.isFinite(now)) {
      return { active: true, status: 'active' };
    }

    const start = source.startEpoch == null ? null : Number(source.startEpoch);
    const end = source.endEpoch == null ? null : Number(source.endEpoch);
    const hasStart = Number.isFinite(start);
    const hasEnd = Number.isFinite(end);

    if (hasStart && now < start) {
      return { active: false, status: 'scheduled' };
    }

    if (hasEnd && now >= end) {
      return { active: false, status: 'ended' };
    }

    return { active: true, status: 'active' };
  }

  /**
   * Decide o que fazer para uma linha de brinde convergir para a
   * invariante "no máximo uma linha por nível, com exatamente uma
   * unidade" — a mesma regra de negócio que o caminho do coordenador
   * (GiftCartMutator.convergeLevelToSingleGift) e o caminho legado
   * (GiftSystemManager.convergeGiftLineQuantities) precisam aplicar
   * igualmente (CART-07). Função pura: recebe as linhas de brinde já
   * filtradas para UM nível e devolve o plano de ação, sem executar nada
   * — quem chama decide como enviar as mudanças (gateway ou fetch cru).
   *
   * Critério de sobrevivência: a linha cujo variant_id bate com
   * preferredVariantId (quando informado), senão a primeira da lista —
   * o mesmo critério que o coordenador já usava antes desta unificação.
   */
  function planGiftLineConvergence(input) {
    const source = isRecord(input) ? input : {};
    const lines = Array.isArray(source.lines) ? source.lines : [];

    if (lines.length === 0) {
      return {
        lines: [],
        survivorKey: null,
        removeLineKeys: [],
        quantityFix: null,
        alreadyConverged: true,
      };
    }

    const preferredVariantId = source.preferredVariantId;
    const survivor = (
      preferredVariantId != null &&
      lines.find((line) => (
        isRecord(line) && String(line.variant_id) === String(preferredVariantId)
      ))
    ) || lines[0];

    const removeLineKeys = lines
      .filter((line) => line !== survivor && line.key !== survivor.key)
      .map((line) => line.key);

    const quantityFix = survivor.quantity === 1
      ? null
      : { lineKey: survivor.key, quantity: 1 };

    return {
      lines,
      survivorKey: survivor.key,
      removeLineKeys,
      quantityFix,
      alreadyConverged: removeLineKeys.length === 0 && quantityFix === null,
    };
  }

  return {
    DECISION_STORAGE_KEY,
    normalizeGiftOptions,
    buildOptionSignature,
    resolveOptionCardinality,
    getNoneDecision,
    recordNoneDecision,
    clearNoneDecision,
    resolveLevelState,
    resolveTriggerProgress,
    resolveTargetLevel,
    sortPendingLevels,
    shouldBlockCheckout,
    resolveCampaignSchedule,
    planGiftLineConvergence,
  };
});
